<template>
  <div class="barcodes-panel">
    <!-- State pills + search -->
    <div class="bp-panel-header">
      <div class="bp-panel-filter d-flex align-items-center">
        <label class="mr-1 mb-0">{{ $t('State') }}:</label>
        <v-select
          v-model="filter.state"
          :options="tabs"
          :clearable="false"
          :searchable="false"
          :reduce="opt => opt.state"
          class="bp-state-select"
          style="min-width: 200px"
          @input="onStateChange"
        >
          <template #option="{ label, state }">
            <div class="d-flex align-items-center">
              <span class="bp-panel-tab__dot mr-50" :class="`bp-panel-tab__dot--${state}`"></span>
              <span>{{ label }}</span>
            </div>
          </template>
          <template #selected-option="{ label, state }">
            <div class="d-flex align-items-center">
              <span class="bp-panel-tab__dot mr-50" :class="`bp-panel-tab__dot--${state}`"></span>
              <span>{{ label }}</span>
            </div>
          </template>
        </v-select>
      </div>

      <b-input-group class="bp-panel-search">
        <b-input-group-prepend is-text>
          <feather-icon icon="SearchIcon" size="14" />
        </b-input-group-prepend>
        <b-form-input
          v-model="searchBarcode"
          :placeholder="$t('Search barcode...')"
          @keyup.enter="applySearch"
          @input="debouncedSearch"
        />
        <b-input-group-append
          v-if="searchBarcode"
          is-text
          class="cursor-pointer"
          role="button"
          :aria-label="$t('Clear search')"
          @click="clearSearch"
        >
          <feather-icon icon="XIcon" size="14" />
        </b-input-group-append>
      </b-input-group>
    </div>

    <!-- Error state -->
    <div v-if="loadError && !isLoading" class="bp-panel-error">
      <feather-icon icon="AlertCircleIcon" size="40" class="bp-panel-error__icon" />
      <h5 class="mb-50">{{ $t('Could not load barcodes') }}</h5>
      <p class="text-muted mb-1">{{ loadError }}</p>
      <b-button variant="outline-primary" size="sm" @click="loadItems">
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
        {{ $t('Retry') }}
      </b-button>
    </div>

    <vue-good-table
      v-else
      mode="remote"
      styleClass="vgt-table bp-panel-table"
      :sort-options="{ enabled: false }"
      :total-rows="totalRecords"
      :is-loading.sync="isLoading"
      :rows="rows"
      :columns="columns"
      :pagination-options="paginationOptions"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <table-no-result-found slot="emptystate" />
      <template slot="loadingContent">
        <span class="vgt-loading__content">{{ $t('Loading...') }}</span>
      </template>
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'barcode'" class="bp-panel-barcode">
          {{ props.row.barcode }}
        </span>

        <span v-else-if="props.column.field === 'state'">
          <b-badge :variant="`light-${stateVariant(props.row.state)}`" class="bp-panel-state-badge">
            <feather-icon :icon="stateIcon(props.row.state)" size="12" class="mr-25" />
            {{ stateLabel(props.row.state) }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'consignment_id'">
          <router-link
            v-if="props.row.consignment_id"
            :to="{
              name: 'consignments_detail',
              params: { id: props.row.consignment_id }
            }"
            class="bp-panel-ref-link"
          >
            #{{ props.row.consignment_id }}
          </router-link>
          <span v-else class="bp-panel-empty">—</span>
        </span>

        <span v-else-if="props.column.field === 'package_id'">
          <span v-if="props.row.package_id" class="bp-panel-ref">
            #{{ props.row.package_id }}
          </span>
          <span v-else class="bp-panel-empty">—</span>
        </span>

        <span
          v-else-if="isDateField(props.column.field)"
          :class="{ 'bp-panel-empty': !props.row[props.column.field] }"
        >
          {{ formatDateTime(props.row[props.column.field]) }}
        </span>

        <span v-else-if="props.column.field === 'skip_reason'">
          <b-badge
            v-if="props.row.skip_reason"
            variant="light-secondary"
            class="bp-panel-skip-pill"
          >
            {{ props.row.skip_reason }}
          </b-badge>
          <span v-else class="bp-panel-empty">—</span>
        </span>

        <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BButton,
  BFormInput,
  BBadge,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import vSelect from 'vue-select';
import debounce from 'lodash.debounce';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';
import TableNoResultFound from '@/components/TableNoResultFound.vue';
import { title } from '@core/utils/filter';
import barcodePoolFormatters from '@/views/pages/barcode-pool/barcodePoolFormatters.mixin';
import { BARCODE_STATE_ICON, BARCODE_STATE_VARIANT } from '@/constants/barcodePool';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const STATES = Object.freeze(['available', 'reserved', 'used', 'skipped']);
const DATE_FIELDS = Object.freeze(['reserved_at', 'used_at', 'released_at']);

export default {
  name: 'BarcodeRangeBarcodesPanel',

  components: {
    BButton,
    BFormInput,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    VueGoodTable,
    TableNoResultFound,
    vSelect
  },

  mixins: [helper, barcodePoolFormatters],

  props: {
    range: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isLoading: true,
      loadError: null,
      rows: [],
      totalRecords: 0,
      searchBarcode: '',
      requestToken: 0,
      filter: {
        page: 1,
        limit: DEFAULT_PAGE_SIZE,
        state: 'all'
      }
    };
  },

  computed: {
    ...mapGetters('auth', ['isAdmin']),

    rangeId() {
      return this.range.id;
    },

    tabs() {
      return [
        { state: 'all', label: this.$t('All') },
        ...STATES.map(s => ({ state: s, label: this.$t(title(s)) }))
      ];
    },

    columns() {
      return [
        { label: this.$t('Barcode'), field: 'barcode', width: '220px' },
        { label: this.$t('State'), field: 'state', width: '160px' },
        { label: this.$t('Consignment'), field: 'consignment_id' },
        { label: this.$t('Package'), field: 'package_id' },
        { label: this.$t('Reserved At'), field: 'reserved_at' },
        { label: this.$t('Used At'), field: 'used_at' },
        { label: this.$t('Released At'), field: 'released_at' },
        { label: this.$t('Skip Reason'), field: 'skip_reason' }
      ];
    },

    paginationOptions() {
      return this.buildPaginationOptions(this.filter.limit);
    }
  },

  created() {
    this.debouncedSearch = debounce(this.applySearch, SEARCH_DEBOUNCE_MS);
  },

  beforeDestroy() {
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  },

  mounted() {
    this.loadItems();
  },

  methods: {
    isDateField(field) {
      return DATE_FIELDS.includes(field);
    },

    stateVariant(state) {
      return BARCODE_STATE_VARIANT[state] || 'secondary';
    },

    stateIcon(state) {
      return BARCODE_STATE_ICON[state] || 'CircleIcon';
    },

    stateLabel(state) {
      return this.$t(title(state));
    },

    tabCount(state) {
      if (state === 'all') return this.range.total_count || 0;
      return this.range[`${state}_count`] || 0;
    },

    onStateChange(state) {
      this.filter.state = state;
      this.filter.page = 1;
      this.loadItems();
    },

    applySearch() {
      this.filter.page = 1;
      this.loadItems();
    },

    clearSearch() {
      this.searchBarcode = '';
      this.applySearch();
    },

    onPageChange({ currentPage }) {
      this.filter.page = currentPage;
      this.loadItems();
    },

    onPerPageChange({ currentPerPage }) {
      this.filter.page = 1;
      this.filter.limit = currentPerPage;
      this.loadItems();
    },

    loadItems() {
      const params = { ...this.filter };
      if (this.searchBarcode) params.barcode = this.searchBarcode;

      this.requestToken += 1;
      const token = this.requestToken;
      this.isLoading = true;
      this.loadError = null;

      const apiCall = this.isAdmin
        ? useService.barcodePoolService.listBarcodeRangeBarcodesAdmin(this.rangeId, params)
        : useService.barcodePoolService.listBarcodeRangeBarcodesCustomer(this.rangeId, params);

      apiCall
        .then(response => {
          if (token !== this.requestToken) return;
          this.rows = response.data || [];
          this.totalRecords = response.meta?.total || 0;
        })
        .catch(messages => {
          if (token !== this.requestToken) return;
          this.loadError = this.extractErrorMessage(messages);
          this.rows = [];
          this.totalRecords = 0;
        })
        .finally(() => {
          if (token !== this.requestToken) return;
          this.isLoading = false;
        });
    }
  }
};
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.barcodes-panel {
  .bp-panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid $border-color;

    .dark-layout & {
      border-bottom-color: $theme-dark-border-color;
    }
  }

  .bp-panel-tabs {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
  }

  .bp-panel-tab {
    background: transparent;
    border: 1px solid transparent;
    padding: 0.45rem 0.85rem;
    border-radius: 1.5rem;
    font-size: 0.82rem;
    font-weight: 500;
    color: $text-muted;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;

    &:hover {
      background: $body-bg;
      color: $headings-color;
    }

    &__dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      display: inline-block;

      &--all {
        background: $text-muted;
      }
      &--available {
        background: $success;
      }
      &--reserved {
        background: $warning;
      }
      &--used {
        background: $primary;
      }
      &--skipped {
        background: $secondary;
      }
    }

    &__count {
      font-variant-numeric: tabular-nums;
      background: rgba($text-muted, 0.12);
      color: $text-muted;
      padding: 0.1rem 0.45rem;
      border-radius: 0.75rem;
      font-size: 0.72rem;
      font-weight: 600;
      transition: background-color 0.18s ease, color 0.18s ease;
    }

    &--active {
      color: inherit;
    }

    &--all.bp-panel-tab--active {
      background: rgba($text-muted, 0.12);
      color: $headings-color;
    }
    &--available.bp-panel-tab--active {
      background: rgba($success, 0.14);
      color: $success;

      .bp-panel-tab__count {
        background: rgba($success, 0.22);
        color: $success;
      }
    }
    &--reserved.bp-panel-tab--active {
      background: rgba($warning, 0.14);
      color: darken($warning, 8%);

      .bp-panel-tab__count {
        background: rgba($warning, 0.22);
        color: darken($warning, 8%);
      }
    }
    &--used.bp-panel-tab--active {
      background: rgba($primary, 0.14);
      color: $primary;

      .bp-panel-tab__count {
        background: rgba($primary, 0.22);
        color: $primary;
      }
    }
    &--skipped.bp-panel-tab--active {
      background: rgba($secondary, 0.18);
      color: $secondary;

      .bp-panel-tab__count {
        background: rgba($secondary, 0.3);
        color: $secondary;
      }
    }

    .dark-layout &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: $theme-dark-headings-color;
    }
  }

  .bp-panel-search {
    width: 320px;
    max-width: 100%;

    .input-group-text {
      background: transparent;
      border-right: none;
    }

    .form-control {
      border-left: none;
      border-right: none;
    }
  }

  .bp-panel-table {
    th {
      text-transform: uppercase;
      font-size: 0.7rem;
      letter-spacing: 0.05em;
      color: $text-muted;
      font-weight: 600;
    }

    td {
      vertical-align: middle;
      padding: 0.9rem 0.75rem !important;
      transition: background-color 0.15s ease;
    }

    tbody tr:hover td {
      background: rgba($primary, 0.04);
    }

    .dark-layout & tbody tr:hover td {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  .bp-panel-barcode {
    font-family: $font-family-monospace;
    font-weight: 600;
  }

  .bp-panel-ref-link {
    font-family: $font-family-monospace;
    color: $primary;
    font-weight: 500;
    text-decoration: underline;
    text-decoration-color: rgba($primary, 0.4);
    text-underline-offset: 2px;
    transition: color 0.15s ease, text-decoration-color 0.15s ease;

    &:hover {
      color: darken($primary, 10%);
      text-decoration-color: $primary;
    }
  }

  .bp-panel-ref {
    font-family: $font-family-monospace;
    color: $body-color;
  }

  .bp-panel-state-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.7rem;
    font-weight: 500;
    font-size: 0.75rem;
    min-width: 110px;
    justify-content: center;
  }

  .bp-panel-skip-pill {
    font-family: $font-family-monospace;
    font-size: 0.7rem;
    padding: 0.25rem 0.55rem;
  }

  .bp-panel-empty {
    color: $text-muted;
    opacity: 0.4;
    user-select: none;
  }

  .bp-panel-error {
    padding: 3rem 2rem;
    text-align: center;

    &__icon {
      color: $danger;
      opacity: 0.7;
      margin-bottom: 1rem;
    }

    h5 {
      font-weight: 600;
    }
  }
}
</style>
